import { useState } from "react";
import { Zap, Link, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface QuickRepliesProps {
  onSelect: (text: string) => void;
  companionProfiles?: { id: string; name: string }[];
}

const defaultReplies = [
  "Thank you for your enquiry! Let me check availability for you.",
  "Yes, she is available tonight. Would you like to book?",
  "Our rates start from \u00a3200/hr. Shall I provide more details?",
  "I'll send you her profile now. One moment please.",
  "Booking confirmed! You'll receive a tracking code shortly.",
  "She's currently unavailable but will be free later today.",
  "Would you prefer incall or outcall?",
  "We require a minimum 1-hour booking. Is that OK?",
];

export function QuickReplies({ onSelect, companionProfiles = [] }: QuickRepliesProps) {
  const [isOpen, setIsOpen] = useState(false);

  const profileLinks = companionProfiles.map(c => ({
    text: `View ${c.name}'s profile: ${window.location.origin}/companion/${c.id}`,
    label: c.name,
  }));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
        title="Quick replies"
      >
        <Zap size={18} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-20 left-3 right-3 sm:absolute sm:bottom-12 sm:left-0 sm:right-auto w-auto sm:w-[280px] max-h-[300px] overflow-y-auto bg-[#141414] border border-gray-800 rounded-xl shadow-2xl z-[200]"
          >
            <div className="p-2 border-b border-gray-800 flex items-center justify-between">
              <span className="text-[9px] text-gray-500 uppercase tracking-widest">Quick Replies</span>
              <button type="button" onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-white">
                <X size={12} />
              </button>
            </div>

            {profileLinks.length > 0 && (
              <div className="p-2 border-b border-gray-800">
                <span className="text-[8px] text-[#D4AF37] uppercase tracking-widest px-1">Companion Profiles</span>
                {profileLinks.map((link, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { onSelect(link.text); setIsOpen(false); }}
                    className="w-full text-left px-2 py-1.5 text-xs text-gray-300 hover:bg-[#1A1A1A] rounded flex items-center gap-2"
                  >
                    <Link size={10} className="text-[#D4AF37] flex-shrink-0" />
                    {link.label}
                  </button>
                ))}
              </div>
            )}

            <div className="p-2">
              <span className="text-[8px] text-[#D4AF37] uppercase tracking-widest px-1">Common Replies</span>
              {defaultReplies.map((reply, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { onSelect(reply); setIsOpen(false); }}
                  className="w-full text-left px-2 py-1.5 text-xs text-gray-300 hover:bg-[#1A1A1A] rounded truncate"
                >
                  {reply}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
